import React, { useState } from 'react'
import { FiUser, FiCheck, FiX } from 'react-icons/fi'

interface NameValidationProps {
    value: string
    onChange: (value: string) => void
    error?: string
    required?: boolean
    placeholder?: string
    className?: string
    showValidation?: boolean
    label?: string
    fieldName?: string
    autoComplete?: string
}

export const NameValidation: React.FC<NameValidationProps> = ({
    value,
    onChange,
    error,
    required = true,
    placeholder = "Enter your name",
    className = "",
    showValidation = true,
    label = "Name",
    fieldName = "name",
    autoComplete = "name"
}) => {
    const [hasStartedTyping, setHasStartedTyping] = useState(false)

    // Name validation rules
    const validations = [
        {
            label: 'At least 3 characters',
            isValid: value.length >= 3,
            check: (name: string) => name.length >= 3
        },
        {
            label: 'No numbers',
            isValid: !/\d/.test(value),
            check: (name: string) => !/\d/.test(name)
        },
        {
            label: 'Only letters and spaces',
            isValid: /^[a-zA-Z\s]*$/.test(value),
            check: (name: string) => /^[a-zA-Z\s]*$/.test(name)
        },
        {
            label: 'No leading/trailing spaces',
            isValid: value.length === 0 || (value === value.trim() && value.length > 0),
            check: (name: string) => name.length === 0 || (name === name.trim() && name.length > 0)
        },
        {
            label: 'Maximum 50 characters',
            isValid: value.length <= 50,
            check: (name: string) => name.length <= 50
        }
    ]

    // Check if name has validation errors
    const hasValidationErrors = hasStartedTyping && value.length > 0 && !validations.every(v => v.isValid)

    // Check if all validations are met (requirements should be hidden)
    const allValidationsMet = hasStartedTyping && value.length > 0 && validations.every(v => v.isValid)

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!hasStartedTyping) {
            setHasStartedTyping(true)
        }
        // Only allow letters and spaces, prevent multiple consecutive spaces
        let newValue = e.target.value.replace(/[^a-zA-Z\s]/g, '')
        newValue = newValue.replace(/\s{2,}/g, ' ')
        onChange(newValue)
    }

    const getValidationIcon = (isValid: boolean) => {
        return isValid ? (
            <FiCheck className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
        ) : (
            <FiX className="w-3 h-3 text-rose-600 dark:text-rose-400" />
        )
    }

    const getValidationBadgeClasses = (isValid: boolean) => {
        return `inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-300 ${isValid
            ? 'bg-emerald-100 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800'
            : 'bg-rose-100 text-rose-700 border border-rose-200 dark:bg-rose-900/30 dark:text-rose-400 dark:border-rose-800'
            }`
    }

    return (
        <div className={`space-y-4 ${className}`}>
            {/* Label */}
            {label && (
                <label
                    htmlFor={fieldName}
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                    {label}
                    {required && <span className="text-red-500 ml-1">*</span>}
                </label>
            )}

            {/* Name Input */}
            <div className="relative">
                {/* User Icon */}
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none z-10">
                    <FiUser className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                </div>

                {/* Input Field */}
                <input
                    id={fieldName}
                    name={fieldName}
                    type="text"
                    value={value}
                    onChange={handleInputChange}
                    onFocus={() => { }}
                    onBlur={() => { }}
                    required={required}
                    autoComplete={autoComplete}
                    maxLength={50}
                    className={`block w-full pl-12 pr-4 py-4 text-base border-2 ${error ? 'border-red-300 focus:border-red-500'
                        : hasValidationErrors ? 'border-rose-300 focus:border-rose-500 dark:border-rose-400 dark:focus:border-rose-500'
                            : 'border-gray-300 focus:border-gray-500 dark:border-gray-600 dark:focus:border-gray-400'
                        } rounded-2xl placeholder-gray-500 text-gray-900 bg-white focus:outline-none focus:ring-0 transition-colors duration-200 dark:bg-gray-800 dark:text-white dark:placeholder-gray-400`}
                    placeholder={placeholder}
                />
            </div>

            {/* Error Message */}
            {error && (
                <div className="text-sm text-red-600 dark:text-red-400 font-medium">
                    {error}
                </div>
            )}

            {/* Validation Badges - only show after user starts typing and hide when all requirements are met */}
            {showValidation && hasStartedTyping && value.length > 0 && !allValidationsMet && (
                <div className="space-y-3">
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {label} Requirements:
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {validations.map((validation, index) => (
                            <div
                                key={index}
                                className={getValidationBadgeClasses(validation.isValid)}
                            >
                                {getValidationIcon(validation.isValid)}
                                <span>{validation.label}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
}

export default NameValidation